import { useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useRoomContext, useAuthenticationContext } from '@src/contexts';
import useRedirectTimer from './useRedirectTimer';

import { getCookie, openURL } from '@src/utils';
import { isWebView } from '@src/helpers';

import { EMeetingRoutes, EWebinarRoutes } from '@src/constants';
import { sendEventToWebView } from '@src/bridge/bridgeMessenger';
import { EWebViewEvents } from '@src/bridge';
import useSendPageFinished from './useSendPageFinished';

const useRejoin = (endMeeting?: boolean) => {
  const { isMobileDevice } = useRoomContext();
  const { isAuthenticated } = useAuthenticationContext();
  const pendingPathRef = useRef<string | null>(null);

  const navigate = useNavigate();

  const location = useLocation();
  const { timeLeft } = useRedirectTimer({});

  const roomType = useMemo(() => location.state?.roomType || 'meeting', [location.state?.roomType]);

  useSendPageFinished({ isMeetingEnded: true });

  useEffect(() => {
    if (endMeeting) return;
    const hasJoined = localStorage.getItem('hasJoined');
    if (!hasJoined) {
      const isWebinar = roomType === 'webinar';
      const room = isWebinar ? 'wid' : 'room';
      const userId = localStorage.getItem('userId');
      const lastRoom = localStorage.getItem('lastRoom');
      const lastRoomTitle = localStorage.getItem('lastRoomTitle');

      if (isWebinar) {
        let path = `${EWebinarRoutes.JOIN}?${room}=${lastRoom}`;
        if (userId) path += `&uid=${userId}`;
        // Only add title parameter if in WebView
        if (isWebView() && lastRoomTitle) path += `&title=${encodeURIComponent(lastRoomTitle)}`;
        navigate(path, { replace: true });
      } else {
        navigate(`${EMeetingRoutes.JOIN}?room=${lastRoom}`, { replace: true });
      }
    }
  }, [endMeeting, navigate, roomType]);

  const handleReturnHome = useCallback(() => {
    localStorage.removeItem('hasJoined');
    sendEventToWebView({ eventType: EWebViewEvents.CLOSE });

    const lastPath = getCookie('lastPath');
    const targetPath = lastPath || '/';

    // Outside iframe: go straight home (full navigation handled by shell)
    if (window.self === window.top) {
      window.location.href = process.env.REACT_APP_ROOT_CONFIG_URL || targetPath;
      return;
    }

    // In iframe: only navigate once auth is ready, to prevent the login flash
    if (!isAuthenticated) {
      pendingPathRef.current = targetPath;
      return;
    }

    navigate(targetPath, { replace: true });
  }, [navigate, isAuthenticated]);

  // Navigate once auth becomes ready
  useEffect(() => {
    if (isAuthenticated && pendingPathRef.current) {
      navigate(pendingPathRef.current, { replace: true });
      pendingPathRef.current = null;
    }
  }, [isAuthenticated, navigate]);

  const handleRejoin = useCallback(() => {
    localStorage.removeItem('hasJoined');
    const isWebinar = roomType === 'webinar';
    const lastRoom = localStorage.getItem('lastRoom');
    const userId = localStorage.getItem('userId');
    const lastRoomTitle = localStorage.getItem('lastRoomTitle');
    const room = isWebinar ? 'wid' : 'room';
    if (!lastRoom) return navigate('/');

    if (isWebinar) {
      let path = `${EWebinarRoutes.JOIN}?${room}=${lastRoom}`;
      if (userId) path += `&uid=${userId}`;
      // Only add title parameter if in WebView
      if (isWebView() && lastRoomTitle) path += `&title=${encodeURIComponent(lastRoomTitle)}`;
      openURL(path);
    } else {
      openURL(`${EMeetingRoutes.JOIN}?${room}=${lastRoom}`);
    }
  }, [roomType, navigate]);

  return {
    handleReturnHome: handleReturnHome,
    handleRejoin: handleRejoin,

    timeLeft: timeLeft.toString(),
    roomType,
    isMobileDevice,
  };
};

export default useRejoin;
